import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { AnimatePresence, motion } from 'framer-motion'

import useIsMobile from '@/hooks/layout/useIsMobile'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/store/useUIStore'

import { SidebarContent, SidebarWrapper } from './Sidebar'

/**
 * Wraps Sidebar content differently depending on if the user is on mobile.
 *
 * @returns {JSX.Element}  The ResponsiveSidebar component
 */
export default function ResponsiveSidebar() {
  const isMobile = useIsMobile()
  const isSidebarOpen = useUIStore(s => s.isSidebarOpen)
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)

  if (isMobile) {
    return (
      <Dialog.Root open={isSidebarOpen} onOpenChange={setSidebarOpen}>
        <Dialog.Portal forceMount>
          <AnimatePresence>
            {isSidebarOpen && (
              <motion.div
                className="fixed inset-0 bg-black/55 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
          </AnimatePresence>

          <Dialog.Content asChild forceMount>
            <motion.div
              id="mobile-sidebar-wrapper"
              className={cn(
                'fixed start-0 z-10 top-0 h-full w-sidebar border-e backdrop-blur-2xl',
                'bg-sidebar text-sidebar-foreground',
                'pt-safe-offset-[-1.5rem]',
              )}
              // animate open/closed instead of conditionally mounting
              animate={isSidebarOpen ? { x: 0 } : { x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
            >
              <SidebarContent id="mobile-sidebar" />
              <VisuallyHidden>
                <Dialog.Title className="sr-only">Mobile Sidebar</Dialog.Title>
                <Dialog.Description className="sr-only" />
              </VisuallyHidden>
            </motion.div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  return (
    <SidebarWrapper>
      <SidebarContent />
    </SidebarWrapper>
  )
}

